// A table or chart cell's body: the spec's side, top and filter to edit, Run,
// and the table. Run goes through the browser's table route (no agent turn, no
// AI credits) and changes only what this cell shows. Props and callbacks only.
import React from 'react'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import InlinePlatinumTable from '../InlinePlatinumTable'
import DisabledReason from '../../system/DisabledReason'
import { specAxes, type SpecAxes } from './notebookMessages'
import type { NotebookCell, NotebookEdit } from './notebookTypes'
import type { NotebookRun } from './useNotebook'

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace'

export interface NotebookTableCellProps {
  cell: NotebookCell
  sessionId: string
  readOnlyReason?: string
  run?: NotebookRun
  running: boolean
  runError: string
  onEdit: (edit: NotebookEdit) => void
  onClearEdit: () => void
  onRun: () => void
}

const FIELDS: Array<{ key: keyof SpecAxes; label: string }> = [
  { key: 'side', label: 'Side (rows)' },
  { key: 'top', label: 'Top (columns)' },
  { key: 'filter', label: 'Filter' },
]

export const NotebookTableCell: React.FC<NotebookTableCellProps> = ({
  cell, sessionId, readOnlyReason, run, running, runError, onEdit, onClearEdit, onRun,
}) => {
  const axes = specAxes(cell)
  const str = (v: unknown) => (typeof v === 'string' ? v : undefined)
  const hasSpec = !!str(cell.data.spec)
  const platinumData = run?.data ?? cell.data.platinumData
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
        {FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            size="small"
            label={label}
            value={axes[key]}
            onChange={(e) => onEdit({ [key]: e.target.value })}
            multiline
            maxRows={4}
            slotProps={{ htmlInput: { readOnly: !!readOnlyReason || !hasSpec, spellCheck: false, 'data-testid': `notebook-${key}` } }}
            sx={{ '& textarea': { fontFamily: MONO, fontSize: 12 } }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
        <DisabledReason reason={readOnlyReason ?? (hasSpec ? undefined : 'This table has no spec to run.')}>
          <Button size="small" variant="contained" color="info" onClick={onRun} disabled={running} data-testid="notebook-run">
            {running ? 'Running…' : 'Run'}
          </Button>
        </DisabledReason>
        {cell.edit && (
          <DisabledReason reason={readOnlyReason}>
            <Button size="small" color="info" onClick={onClearEdit} data-testid="notebook-reset">
              Reset
            </Button>
          </DisabledReason>
        )}
        {run && (
          <Typography variant="caption" color="text.secondary">
            Showing your re-run. The chat still shows the agent’s table.
          </Typography>
        )}
      </Box>
      {runError && <Alert severity="error" sx={{ mt: 1 }} data-testid="notebook-run-error">{runError}</Alert>}
      {cell.needsRun && !run ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="notebook-needs-run">
          This table was made in a batch, so its figures are in the chat’s files. Run it to see them here.
        </Typography>
      ) : (
        <InlinePlatinumTable
          key={run ? `run-${run.seq}` : 'event'}
          platinumData={platinumData}
          title={cell.title}
          customer={str(cell.data.customer)}
          job={str(cell.data.job)}
          spec={run?.spec ?? str(cell.data.spec)}
          sessionId={sessionId}
          defaultViewMode={cell.kind === 'chart' ? 'graph' : undefined}
          defaultChartType={str(cell.data.chartType)}
        />
      )}
    </Box>
  )
}

export default NotebookTableCell
